/**
 * The one paragraph under a collection page's H1, and the count it must state.
 *
 * The crawler-text guard (lib/crawler-text/guard.ts, checkCollectionPage, run
 * by the HTML render gate in CI) reads a collection page the way Googlebot,
 * Bingbot and the answer engines do and requires the hero to state how many
 * models the page tracks: "704 models tracked by Fridge.com", or "over N" when
 * the count is a floor (FRIDGE-480). The fallback sentence always did. On
 * 10/21/2026 the hand-written definitions of FRIDGE-57 went live on 29
 * collections, and the hero printed the definition INSTEAD of the fallback,
 * so /collections/french-door-refrigerators stopped stating its count and the
 * gate went red on pull request 819.
 *
 * The rule here: whatever the hero leads with (the measured answer-first
 * sentence of a head page, FRIDGE-1434; a definition a person wrote,
 * FRIDGE-57), it still states the count. A lead that already carries it
 * ("across 586 freezers tracked by Fridge.com") is left alone; otherwise the
 * count follows as its own short sentence. Without a lead, the fallback is
 * unchanged. Pure, so the guard can be run against the rendered paragraph in
 * a test (tests/collection-hero-states-count-fridge-766.test.ts).
 */

export interface HeroCountFacts {
  /** The count the hero states: the filtered list's size when filters are active, else the page's total. */
  heroCount: number
  /** The page's total, named inside the filtered form. */
  totalProducts: number
  /** True when the total is the PostgREST row ceiling, a floor, never an exact figure (FRIDGE-480). */
  countIsFloor: boolean
  /** True when the visitor's filters narrow the list the hero describes. */
  heroFiltered: boolean
}

/** What the guard reads as a stated count: a number, a noun, "tracked by Fridge.com". */
export const STATES_TRACKED_COUNT = /\d[\d,]*\s+\S+\s+tracked by Fridge\.com/

/**
 * "704 models tracked by Fridge.com" · "over 1,000 models tracked by Fridge.com" ·
 * "12 models match your filters (of 704 tracked by Fridge.com)" · '' when the page has no count.
 */
export function trackedCountClause(f: HeroCountFacts): string {
  if (!(f.heroCount > 0)) return ''
  const floor = f.countIsFloor && !f.heroFiltered ? 'over ' : ''
  const noun = f.heroCount === 1 ? 'model' : 'models'
  const tail = f.heroFiltered
    ? ` match your filters (of ${f.countIsFloor ? 'over ' : ''}${f.totalProducts.toLocaleString()} tracked by Fridge.com)`
    : ' tracked by Fridge.com'
  return `${floor}${f.heroCount.toLocaleString()} ${noun}${tail}`
}

export interface HeroIntroParts {
  /** A head page's measured answer-first sentence (FRIDGE-1434), or null. */
  answerFirst: string | null
  /** The stored intro a person wrote (FRIDGE-57), or the collection's description, or null. */
  description: string | null
  /** The collection's title, lower-cased with brand casing restored, for the fallback. */
  titleLower: string
}

/** The hero paragraph's text. */
export function heroIntroText(parts: HeroIntroParts, facts: HeroCountFacts): string {
  const clause = trackedCountClause(facts)
  const lead = [parts.answerFirst, parts.description].filter(Boolean).join(' ')
  if (lead) {
    if (!clause || STATES_TRACKED_COUNT.test(lead)) return lead
    return `${lead} ${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`
  }
  return `Compare prices on ${parts.titleLower} from major online appliance retailers${clause ? ` — ${clause}` : ''}.`
}
